import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { loadWithRetry } from '../lib/loadWithRetry.js';
import { fetchIssues, formatIssueMonth, issueContent, issuePath } from '../lib/newsletter.js';
import { ArrowRightIcon } from '../components/icons/Icons.jsx';

export default function Newsletter() {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useAuth();
  const [issues, setIssues] = useState(null);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIssues(null);
    setError(null);
    loadWithRetry(fetchIssues, { label: 'newsletter' })
      .then((rows) => !cancelled && setIssues(rows))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  // Group newest-first issues by year so the list reads like an archive.
  const byYear = useMemo(() => {
    if (!issues) return [];
    const map = new Map();
    for (const issue of issues) {
      if (!map.has(issue.year)) map.set(issue.year, []);
      map.get(issue.year).push(issue);
    }
    return [...map.entries()];
  }, [issues]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
        <button type="button" className="btn-primary mt-6" onClick={() => setRetryKey((k) => k + 1)}>
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }
  if (!issues) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  const publishedCount = issues.filter((i) => i.status === 'published').length;

  return (
    <div className="mx-auto max-w-4xl px-6 pb-20 pt-28">
      <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label">{t('home.tiles.newsletter.title')}</div>
          <h1 className="font-serif text-4xl font-semibold text-cream">{t('newsletter.heading')}</h1>
          <p className="mt-2 max-w-2xl text-ink-secondary">{t('newsletter.subheading')}</p>
        </div>
        {isAdmin && (
          <Link to="/newsletter/new" className="btn-primary">
            {t('newsletter.add')}
          </Link>
        )}
      </header>

      {issues.length === 0 || (!isAdmin && publishedCount === 0) ? (
        <p className="rounded-lg border border-white/10 bg-navy-base/40 p-10 text-center text-ink-secondary">
          {t('newsletter.empty')}
        </p>
      ) : (
        byYear.map(([year, rows]) => (
          <section key={year} className="mb-10">
            <h2 className="mb-4 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-gold">{year}</h2>
            <div className="flex flex-col gap-3">
              {rows.map((issue) => (
                <IssueRow key={issue.id} issue={issue} locale={i18n.resolvedLanguage} t={t} />
              ))}
            </div>
          </section>
        ))
      )}

      <aside className="mt-12 rounded-xl border border-gold/20 bg-navy-base/40 p-6 text-sm text-ink-secondary">
        <h2 className="mb-2 font-serif text-lg text-cream">{t('newsletter.subscribe.title')}</h2>
        <p>{t('newsletter.subscribe.body')}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/auth" className="btn-ghost text-sm">
            {t('nav.register')}
          </Link>
          <Link to="/whats-new/new" className="btn-ghost text-sm">
            {t('newsletter.subscribe.submitNews')}
          </Link>
        </div>
      </aside>
    </div>
  );
}

function IssueRow({ issue, locale, t }) {
  const isDraft = issue.status !== 'published';
  const content = issueContent(issue, locale);
  return (
    <Link to={issuePath(issue)} className="card block !p-6">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-gold">
          {formatIssueMonth(issue.year, issue.month, locale)}
        </span>
        {isDraft && (
          <span className="rounded-full border border-gold/40 bg-navy-mid/40 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-[0.12em] text-gold-light">
            {t('newsletter.status.draft')}
          </span>
        )}
      </div>
      <h3 className="mt-1 font-serif text-2xl font-semibold text-cream">{content.title}</h3>
      {content.summary && <p className="mt-2 line-clamp-3 text-sm text-ink-secondary">{content.summary}</p>}
      <span className="card-link mt-4">
        {t('newsletter.readIssue')}
        <ArrowRightIcon />
      </span>
    </Link>
  );
}
